import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Check, Palette } from "lucide-react";
import { api } from "./api";
import { useAuth } from "./auth";
import { useBrand } from "./brand";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";

export const THEMES = [
  { id: "aegis", name: "Aegis Lime", desc: "Default: neon lime on black", bg: "#020303", panel: "#07090A", accent: "#CCFF00", accent2: "#37E5D7" },
  { id: "aegis-gold", name: "Aegis Gold", desc: "Gold leaf on deep navy", bg: "#0A1626", panel: "#0F1E33", accent: "#D4A537", accent2: "#7FB3E8" },
  { id: "hud-cyan", name: "HUD Cyan", desc: "Electric cyan on navy", bg: "#06121F", panel: "#0B1B2C", accent: "#00E5FF", accent2: "#7C9CFF" },
  { id: "forest", name: "Forest Calm", desc: "Calming evergreen, easier on the eyes", bg: "#04140E", panel: "#0A1F17", accent: "#3DDC84", accent2: "#A3E635" },
  { id: "sunset", name: "Sunset Warm", desc: "Warm amber on deep maroon, for the late shift", bg: "#1C0A10", panel: "#2A1118", accent: "#FFB020", accent2: "#FF6B6B" },
  { id: "arctic", name: "Arctic", desc: "Cool ice blue, high focus", bg: "#07131D", panel: "#0D1D2A", accent: "#8FD3FF", accent2: "#C4F1FF" },
  { id: "lavender", name: "Lavender", desc: "Soft violet, relaxed concentration", bg: "#0F0B1E", panel: "#18122D", accent: "#A78BFA", accent2: "#F0ABFC" },
  { id: "mocha", name: "Mocha", desc: "Espresso and cream, cozy warmth", bg: "#160F0B", panel: "#221813", accent: "#C8955F", accent2: "#E8D3B9" },
  { id: "aegis-azure", name: "Aegis Azure", desc: "Heraldic azure on midnight", bg: "#050B1A", panel: "#0A1530", accent: "#2F8CFF", accent2: "#7DD3FC" },
  { id: "neon-tokyo", name: "Neon Tokyo", desc: "Magenta-cyan synthwave, maximum HUD", bg: "#12001F", panel: "#1D0632", accent: "#FF2BD6", accent2: "#00F0FF" },
  { id: "matrix", name: "Matrix Green", desc: "Phosphor green on black, terminal mode", bg: "#000000", panel: "#050A05", accent: "#00FF41", accent2: "#7CFC00" },
  { id: "amber-crt", name: "Amber CRT", desc: "Vintage amber-monitor warmth", bg: "#120D05", panel: "#1A140A", accent: "#FFB000", accent2: "#FFD27A" },
  { id: "midnight-steel", name: "Midnight Steel", desc: "Deep navy and steel blue, maritime feel", bg: "#08111C", panel: "#0E1A28", accent: "#5B9BD5", accent2: "#A9C6E3" },
  { id: "rose-quartz", name: "Rose Quartz", desc: "Muted pink on charcoal, gentle accent", bg: "#1A1617", panel: "#241E20", accent: "#E89AB5", accent2: "#F5C6D6" },
  { id: "carbon", name: "Carbon Fiber", desc: "Slate-on-slate stealth, minimal accent", bg: "#151719", panel: "#1C1F22", accent: "#8A99AA", accent2: "#C2CCD6" },
];
const LS = "aegis-theme";
const rgb = (h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
const mix = (a, b, t) => "#" + rgb(a).map((x, i) => Math.round(x + (rgb(b)[i] - x) * t).toString(16).padStart(2, "0")).join("");
const hsl = (h) => {
  const [r, g, b] = rgb(h).map((x) => x / 255);
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b), l = (mx + mn) / 2, d = mx - mn;
  const s = d ? d / (1 - Math.abs(2 * l - 1)) : 0;
  const hh = !d ? 0 : mx === r ? ((g - b) / d) % 6 : mx === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return `${Math.round((hh * 60 + 360) % 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

export const applyTheme = (t, brandAccent) => {
  const accent = t.id === "aegis" && brandAccent ? brandAccent : t.accent;
  const v = {
    "--brand": accent, "--brand-rgb": rgb(accent).join(" "), "--accent2": t.accent2, "--accent2-rgb": rgb(t.accent2).join(" "),
    "--base": t.bg, "--panel": t.panel, "--surface": mix(t.bg, t.panel, 0.6), "--sidebar": mix(t.bg, "#000000", 0.3),
    "--line": mix(t.panel, accent, 0.1), "--divider": mix(t.panel, accent, 0.05), "--field": mix(t.bg, "#000000", 0.2), "--fieldline": mix(t.panel, accent, 0.16),
    "--background": hsl(t.bg), "--card": hsl(t.panel), "--popover": hsl(t.panel), "--border": hsl(mix(t.panel, accent, 0.1)),
    "--input": hsl(mix(t.panel, accent, 0.16)), "--primary": hsl(accent), "--ring": hsl(accent), "--accent": hsl(t.accent2),
  };
  const root = document.documentElement;
  root.classList.add("theme-fade");
  Object.entries(v).forEach(([k, val]) => root.style.setProperty(k, val));
  root.dataset.theme = t.id;
  clearTimeout(applyTheme.t);
  applyTheme.t = setTimeout(() => root.classList.remove("theme-fade"), 350);
};

const Ctx = createContext({ theme: THEMES[0], setTheme: () => {} });

export const ThemeProvider = ({ children }) => {
  const { user, setUser } = useAuth();
  const { brand } = useBrand();
  const [local, setLocal] = useState(() => localStorage.getItem(LS));
  const id = user ? user.theme || brand?.default_theme || "aegis" : local || brand?.default_theme || "aegis";
  const theme = useMemo(() => THEMES.find((t) => t.id === id) || THEMES[0], [id]);
  useEffect(() => { applyTheme(theme, brand?.accent_color); }, [theme, brand?.accent_color]);
  const setTheme = useCallback((tid) => {
    localStorage.setItem(LS, tid); setLocal(tid);
    if (user) { setUser({ ...user, theme: tid }); api.post("/auth/theme", { theme: tid }).catch(() => {}); }
  }, [user, setUser]);
  return <Ctx.Provider value={{ theme, setTheme }}>{children}</Ctx.Provider>;
};

export const useTheme = () => useContext(Ctx);

export const ThemePicker = ({ compact }) => {
  const { theme, setTheme } = useTheme();
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="flex items-center gap-2 px-2.5 py-1.5 border border-white/10 hover:border-white/30 transition-colors" data-testid="theme-picker-btn" title="Change color theme">
          <span className="w-3 h-3 rounded-full" style={{ background: theme.accent, boxShadow: `0 0 8px ${theme.accent}` }} />
          {!compact && <span className="hidden md:inline font-mono text-[10px] tracking-wider text-white/70" data-testid="theme-current-name">{theme.name}</span>}
          {compact && <Palette className="w-3.5 h-3.5 text-white/60" />}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-1 bg-panel border-white/15 max-h-[70vh] overflow-y-auto" data-testid="theme-menu">
        <p className="label-xs px-3 pt-2 pb-1">Color theme</p>
        {THEMES.map((t) => (
          <button key={t.id} onClick={() => { setTheme(t.id); setOpen(false); }} data-testid={`theme-option-${t.id}`}
            className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-white/[0.05] transition-colors ${t.id === theme.id ? "bg-white/[0.04]" : ""}`}>
            <span className="relative w-6 h-6 shrink-0 border border-white/15" style={{ background: t.bg }}>
              <span className="absolute inset-1.5 rounded-full" style={{ background: t.accent }} />
            </span>
            <span className="flex-1 min-w-0"><span className="block text-sm">{t.name}</span><span className="block text-[11px] text-white/45 truncate">{t.desc}</span></span>
            {t.id === theme.id && <Check className="w-4 h-4 text-brand" data-testid="theme-active-check" />}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
};
